import React, { useState, useEffect, useRef } from 'react';

// Deck picker for the Add/Edit Card form.
//
// A native <select> hands the choice to the phone's own picker, which becomes a
// full-screen sheet once there are many decks (no space above or below). This list
// opens inline instead and is capped to a fraction of the screen with its own scroll,
// so the rest of the form stays visible around it.
const NONE = { id: '', label: '📂 No Deck (Unassigned)' };

/**
 * @param {{ decks: Array<{id: string, name: string}>, value: string, onChange: (id: string) => void, className?: string }} props
 */
export default function DeckSelect({ decks, value, onChange, className = '' }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const options = [NONE, ...decks.map(d => ({ id: d.id, label: `📁 ${d.name}` }))];
  const selected = options.find(o => o.id === (value || '')) || NONE;

  useEffect(() => {
    if (!open) return undefined;
    // Keep the whole list on screen inside the scrolling form (no-op where unsupported).
    rootRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    const close = (e) => {
      if (e.type === 'keydown' && e.key !== 'Escape') return;
      if (e.type === 'mousedown' && rootRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between gap-2 text-left ${className}`}>
        <span className="truncate">{selected.label}</span>
        <span aria-hidden="true" className="text-ink3 text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label="Deck"
          className="mt-1 max-h-[40vh] overflow-y-auto overscroll-contain rounded-btn border border-line bg-surface">
          {options.map(o => (
            <li
              key={o.id || 'none'}
              role="option"
              aria-selected={o.id === selected.id}
              tabIndex={0}
              onClick={() => { onChange(o.id); setOpen(false); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onChange(o.id);
                  setOpen(false);
                }
              }}
              className={`px-4 py-2 text-sm cursor-pointer truncate hover:bg-inset focus:bg-inset ${
                o.id === selected.id ? 'text-atext font-medium bg-tint' : ''
              }`}>
              {o.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
